import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PortalLayout from '@/components/feature/PortalLayout';
import MovieListItem from '@/components/feature/MovieListItem';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const TYPE_TABS = [
  { key: 'all', label: '全部类别' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
];

const SUGGESTIONS = ['悬疑', '爱情', '古装', '科幻', '迷雾', '动作'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const [input, setInput] = useState(queryParam);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  usePageMeta({
    title: queryParam ? `“${queryParam}”的搜索结果 - 诱人的女员工hd` : '搜索影视 - 诱人的女员工hd影视专区',
    description: `在诱人的女员工hd影视专区搜索“${queryParam || '影视作品'}”，支持按影视名称与演员模糊匹配，快速找到相关电影与电视剧。`,
    keywords: '诱人的女员工hd,影视搜索,电影电视剧,演员信息',
  });

  const results = useMemo(() => {
    const q = queryParam.trim().toLowerCase();
    if (!q) return [];
    return movies.filter((m) => {
      const matched =
        m.title.toLowerCase().includes(q) ||
        m.cast.some((c) => c.toLowerCase().includes(q)) ||
        m.director.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q));
      const okType = tab === 'all' || m.type === tab;
      return matched && okType;
    });
  }, [queryParam, tab]);

  const runSearch = (keyword: string) => {
    const q = keyword.trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  return (
    <PortalLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <nav className="flex items-center gap-1 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-accent-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">搜索</span>
        </nav>

        <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">搜索影视作品</h1>
        <p className="mt-2 text-sm text-foreground-600">输入影视名称、演员或类型关键词，即可快速查找相关作品</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(input);
          }}
          className="mt-5 flex flex-col sm:flex-row gap-3"
        >
          <div className="flex items-center flex-1 bg-background-100 border border-background-300 rounded-md px-3 h-12 focus-within:border-accent-400">
            <span className="w-6 h-6 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：迷雾、林深、悬疑…"
              className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
            />
            {input && (
              <button
                type="button"
                aria-label="清空"
                onClick={() => setInput('')}
                className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-circle-line text-lg"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-8 h-12 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>

        {/* type filter */}
        <div className="mt-5 flex items-center gap-1 px-1 py-1 rounded-full bg-background-200 w-fit">
          {TYPE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                tab === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {queryParam ? (
          <p className="mt-6 text-sm text-foreground-500">
            关键词“<span className="text-accent-600 font-medium">{queryParam}</span>”共找到
            <span className="text-foreground-900 font-medium"> {results.length} </span>部作品
          </p>
        ) : (
          <p className="mt-6 text-sm text-foreground-500">输入关键词开始搜索吧</p>
        )}

        {results.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {results.map((m) => (
              <MovieListItem key={m.id} movie={m} />
            ))}
          </div>
        ) : queryParam ? (
          <div className="mt-6 py-16 text-center bg-background-100 border border-dashed border-background-300 rounded-lg">
            <span className="inline-flex w-14 h-14 md:w-16 md:h-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-4">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="font-heading font-bold text-foreground-900">没有找到相关作品</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐：</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => runSearch(s)}
                  className="px-3 py-1.5 rounded-full bg-background-200 text-foreground-700 text-xs hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </PortalLayout>
  );
}